import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Megaphone, Send, Zap, Play, Trophy, Mail, Save } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { fmt, fmtDateTime } from "@/components/shared";

const inputCls = "w-full bg-slate-900/60 border border-cyan-500/20 rounded-lg px-4 py-2.5 text-slate-100 text-sm focus:outline-none focus:border-cyan-400";

export function EarningsEngineCard() {
  const [st, setSt] = useState(null);
  const [running, setRunning] = useState(false);
  const load = useCallback(() => api.get("/admin/earnings/status").then((r) => setSt(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const run = async () => {
    setRunning(true);
    try {
      const r = await api.post("/admin/earnings/run");
      toast.success(`Earnings cycle complete — ${r.data.credited} investment(s) credited`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRunning(false); }
  };

  return (
    <div className="glass rounded-2xl p-6" data-testid="earnings-engine-card">
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2"><Zap className="w-5 h-5 text-cyan-400" /><h3 className="font-heading text-white">Earnings Engine</h3></div>
        <button onClick={run} disabled={running} data-testid="earnings-run-btn" className="glass glass-hover px-4 py-2 rounded-full text-xs font-semibold text-cyan-300 inline-flex items-center gap-2 disabled:opacity-60">
          {running ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Play className="w-3.5 h-3.5" />} Run due cycle now
        </button>
      </div>
      <p className="text-xs text-slate-500 mb-4">Runs automatically every 10 minutes; each active investment receives 1% of its original principal once per working day (Mon–Fri, never on weekends) until its 200 working days complete; then it is marked MATURED and principal is handled per the plan's maturity rule. Every payout is written to the ROI ledger.</p>
      {st && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-4 text-sm">
          <div><p className="text-xs text-slate-500">Active</p><p className="font-mono text-lg text-white" data-testid="earnings-active">{st.active_count}</p></div>
          <div><p className="text-xs text-slate-500">Locked USDT</p><p className="font-mono text-lg text-white" data-testid="earnings-locked">{fmt(st.total_invested)}</p></div>
          <div><p className="text-xs text-slate-500">Due now</p><p className="font-mono text-lg text-amber-300" data-testid="earnings-due">{st.due_count}</p></div>
          <div><p className="text-xs text-slate-500">Matured</p><p className="font-mono text-lg text-emerald-300" data-testid="earnings-completed">{st.completed_count}</p></div>
          <div><p className="text-xs text-slate-500">Total ROI paid</p><p className="font-mono text-lg text-emerald-300" data-testid="earnings-total-roi">{fmt(st.total_roi)}</p></div>
        </div>
      )}
      {st?.last_run_at && <p className="text-xs text-slate-500 mt-4" data-testid="earnings-last-run">Last credit run: {fmtDateTime(st.last_run_at)} · {st.last_credited} credited</p>}
    </div>
  );
}

export function LeaderboardCloseCard() {
  const [data, setData] = useState(null);
  const [month, setMonth] = useState(() => { const d = new Date(); d.setMonth(d.getMonth() - 1); return d.toISOString().slice(0, 7); });
  const [closing, setClosing] = useState(false);
  const load = useCallback(() => api.get("/admin/leaderboard/awards").then((r) => setData(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const close = async () => {
    setClosing(true);
    try {
      const r = await api.post("/admin/leaderboard/close", { month });
      toast[r.data.already_closed ? "info" : "success"](r.data.already_closed ? `${r.data.month} was already closed` : `${r.data.month} closed — ${r.data.awarded} referrer(s) awarded`);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setClosing(false); }
  };

  return (
    <div className="glass rounded-2xl p-6 lg:col-span-2" data-testid="leaderboard-close-card">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4">
        <div className="flex items-center gap-2"><Trophy className="w-5 h-5 text-amber-300" /><h3 className="font-heading text-white">Referral Leaderboard Awards</h3></div>
        <div className="flex gap-2 items-center">
          <input type="month" data-testid="leaderboard-close-month" value={month} max={(() => { const d = new Date(); d.setMonth(d.getMonth() - 1); return d.toISOString().slice(0, 7); })()} onChange={(e) => setMonth(e.target.value)} className={inputCls + " w-44"} />
          <button onClick={close} disabled={closing || !month} data-testid="leaderboard-close-btn" className="glass glass-hover px-4 py-2 rounded-full text-xs font-semibold text-amber-300 inline-flex items-center gap-2 disabled:opacity-60 whitespace-nowrap">
            {closing ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Trophy className="w-3.5 h-3.5" />} Close month now
          </button>
        </div>
      </div>
      <p className="text-xs text-slate-500 mb-4">Each month closes automatically on the 1st: the top 3 referrers receive their bonus and a trophy badge. Only past months can be closed manually, and each month can only be awarded once.</p>
      {data && (data.items.length ? (
        <div className="overflow-x-auto" data-testid="leaderboard-awards-table">
          <table className="w-full text-sm min-w-[560px]">
            <thead><tr className="text-left text-slate-500 border-b border-white/5">{["Month", "Rank", "User", "Referral rewards", "Bonus paid", "Awarded"].map((h) => <th key={h} className="pb-2 px-2 font-medium">{h}</th>)}</tr></thead>
            <tbody>
              {data.items.slice(0, 12).map((a) => (
                <tr key={a.id} className="border-b border-white/5 last:border-0" data-testid={`award-row-${a.id}`}>
                  <td className="py-2 px-2 text-slate-300">{a.month_label}</td>
                  <td className="py-2 px-2 font-mono text-amber-300">#{a.rank}</td>
                  <td className="py-2 px-2"><p className="text-slate-200">{a.user_name}</p><p className="text-xs text-slate-500">{a.user_email}</p></td>
                  <td className="py-2 px-2 font-mono text-slate-300">{fmt(a.rewards)}</td>
                  <td className="py-2 px-2 font-mono text-emerald-300">{fmt(a.bonus)}</td>
                  <td className="py-2 px-2 text-xs text-slate-500">{fmtDateTime(a.created_at)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <p className="text-sm text-slate-500" data-testid="leaderboard-awards-empty">No months closed yet.</p>)}
    </div>
  );
}

export function EmailDeliveryCard() {
  const [s, setS] = useState(null);
  const [gmail, setGmail] = useState("");
  const [appPw, setAppPw] = useState("");
  const [fromName, setFromName] = useState("AI TECHNOLOGY");
  const [loginEmail, setLoginEmail] = useState(true);
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState(null);
  const [log, setLog] = useState([]);

  const load = useCallback(async () => {
    const [r, l] = await Promise.all([api.get("/admin/settings"), api.get("/admin/settings/email-log")]);
    setS(r.data); setGmail(r.data.smtp_email || ""); setFromName(r.data.email_from_name || "AI TECHNOLOGY");
    setLoginEmail(r.data.login_email_enabled ?? true); setLog(l.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!gmail.trim()) return toast.error("Enter the Gmail address to send from");
    if (!s?.smtp_configured && appPw.replace(/\s/g, "").length !== 16) return toast.error("App Password must be 16 characters");
    setBusy("save");
    try {
      const body = { smtp_email: gmail, email_from_name: fromName, login_email_enabled: loginEmail };
      if (appPw.trim()) body.smtp_app_password = appPw;
      await api.put("/admin/settings", body);
      setAppPw(""); toast.success("Email settings saved — send a test to confirm"); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const test = async () => {
    setBusy("test");
    try { const r = await api.post("/admin/settings/test-email", { to: testTo || undefined }); toast.success(r.data.message); load(); }
    catch (e) { toast.error(formatApiError(e)); load(); }
    finally { setBusy(null); }
  };

  const clear = async () => {
    setBusy("clear");
    try { await api.delete("/admin/settings/smtp"); toast.success("Gmail credentials removed"); setAppPw(""); load(); }
    catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  const live = s?.smtp_configured || s?.resend_configured;
  return (
    <div className="glass rounded-2xl p-6 lg:col-span-2" data-testid="email-delivery-card">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-1">
        <div className="flex items-center gap-2"><Mail className="w-5 h-5 text-cyan-400" /><h3 className="font-heading text-white">Email Delivery</h3></div>
        <span data-testid="email-status" className={`px-3 py-1 rounded-full text-xs font-medium border ${live ? "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" : "bg-amber-500/15 text-amber-300 border-amber-500/30"}`}>
          {s?.smtp_configured ? `Live · Gmail (${s.smtp_email})` : s?.resend_configured ? "Live · Resend" : "Not configured — emails are skipped"}
        </span>
      </div>
      <p className="text-xs text-slate-500 mb-5">Send welcome, login, deposit, withdrawal, 2FA and announcement emails from your own Gmail account. Google Account → Security → 2-Step Verification → App passwords → create one and paste the 16 characters below.</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <div><label className="text-sm text-slate-300 mb-1.5 block">Gmail address</label>
          <input data-testid="smtp-email" type="email" value={gmail} onChange={(e) => setGmail(e.target.value)} placeholder="yourname@gmail.com" className={inputCls} /></div>
        <div><label className="text-sm text-slate-300 mb-1.5 block">App Password {s?.smtp_configured && <span className="text-xs text-slate-500">(saved — enter only to replace)</span>}</label>
          <input data-testid="smtp-password" type="password" value={appPw} onChange={(e) => setAppPw(e.target.value)} placeholder={s?.smtp_configured ? "••••••••••••••••" : "abcd efgh ijkl mnop"} autoComplete="new-password" className={inputCls + " font-mono tracking-widest"} /></div>
        <div><label className="text-sm text-slate-300 mb-1.5 block">Sender name</label>
          <input data-testid="smtp-from-name" value={fromName} onChange={(e) => setFromName(e.target.value)} className={inputCls} /></div>
        <div className="flex items-center gap-3 pt-6">
          <Switch data-testid="login-email-toggle" checked={loginEmail} onCheckedChange={setLoginEmail} />
          <span className="text-sm text-slate-300">Email users on every login (time, IP, device)</span>
        </div>
      </div>
      <div className="flex flex-wrap gap-2 mt-5">
        <button onClick={save} disabled={!!busy} data-testid="smtp-save" className="btn-glow px-5 py-2.5 rounded-full text-sm font-semibold text-slate-950 inline-flex items-center gap-2 disabled:opacity-60">
          {busy === "save" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save email settings
        </button>
        {s?.smtp_configured && <button onClick={clear} disabled={!!busy} data-testid="smtp-clear" className="glass glass-hover px-4 py-2.5 rounded-full text-sm text-red-300 disabled:opacity-60">Remove Gmail</button>}
      </div>
      <div className="mt-5 pt-4 border-t border-white/5 flex flex-col sm:flex-row gap-2">
        <input data-testid="test-email-to" type="email" value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="Send test to (defaults to your admin email)" className={inputCls + " sm:flex-1"} />
        <button onClick={test} disabled={!!busy || !live} data-testid="test-email-send" className="glass glass-hover px-4 py-2.5 rounded-lg text-sm text-cyan-300 inline-flex items-center justify-center gap-2 disabled:opacity-60 whitespace-nowrap">
          {busy === "test" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send test email
        </button>
      </div>
      {log.length > 0 && (
        <div className="mt-4 space-y-1.5" data-testid="email-log">
          <p className="text-xs text-slate-500 uppercase tracking-wider">Recent sends</p>
          {log.slice(0, 5).map((l) => (
            <div key={l.id} className="flex items-center justify-between gap-3 text-xs">
              <span className="text-slate-300 truncate">{l.subject} <span className="text-slate-500">→ {l.to}</span></span>
              <span className={`shrink-0 ${l.error ? "text-red-400" : "text-emerald-300"}`} title={l.error || ""}>{l.error ? "failed" : `sent · ${l.provider}`} · {fmtDateTime(l.created_at)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export function BroadcastCard({ emailEnabled }) {
  const [title, setTitle] = useState("");
  const [message, setMessage] = useState("");
  const [sendEmail, setSendEmail] = useState(false);
  const [sending, setSending] = useState(false);
  const [history, setHistory] = useState([]);
  const load = useCallback(() => api.get("/admin/broadcasts").then((r) => setHistory(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const send = async () => {
    if (!title.trim() || !message.trim()) { toast.error("Title and message are required"); return; }
    setSending(true);
    try {
      const r = await api.post("/admin/broadcast", { title, message, send_email: sendEmail });
      toast.success(`Announcement sent to ${r.data.recipients} user(s)`);
      setTitle(""); setMessage(""); load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setSending(false); }
  };

  return (
    <div className="glass rounded-2xl p-6" data-testid="broadcast-card">
      <div className="flex items-center gap-2 mb-5"><Megaphone className="w-5 h-5 text-cyan-400" /><h3 className="font-heading text-white">Announcements</h3></div>
      <div className="space-y-3">
        <input data-testid="broadcast-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Title (e.g. Scheduled maintenance)" className={inputCls} maxLength={120} />
        <textarea data-testid="broadcast-message" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Message to all active users..." rows={4} className={inputCls} maxLength={2000} />
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="flex items-center gap-3">
            <Switch data-testid="broadcast-email-toggle" checked={sendEmail} disabled={!emailEnabled} onCheckedChange={setSendEmail} />
            <span className="text-sm text-slate-300">Also send by email{!emailEnabled && <span className="text-xs text-slate-500"> (email delivery not configured)</span>}</span>
          </div>
          <button onClick={send} disabled={sending} data-testid="broadcast-send" className="btn-glow px-5 py-2.5 rounded-full text-sm font-semibold text-slate-950 inline-flex items-center gap-2 disabled:opacity-60">
            {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />} Send to all users
          </button>
        </div>
      </div>
      {history.length > 0 && (
        <div className="mt-6 border-t border-white/5 pt-4 space-y-3" data-testid="broadcast-history">
          <p className="text-xs text-slate-500 uppercase tracking-wider">Recent announcements</p>
          {history.slice(0, 5).map((b) => (
            <div key={b.id} className="flex items-start justify-between gap-3 text-sm">
              <div className="min-w-0"><p className="text-slate-200 truncate">{b.title}</p><p className="text-xs text-slate-500 truncate">{b.message}</p></div>
              <div className="text-right shrink-0"><p className="text-xs text-slate-400">{b.recipients} users{b.email_sent ? " · emailed" : ""}</p><p className="text-xs text-slate-600">{fmtDateTime(b.created_at)}</p></div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
